import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { OrderDetailResponse, QuotePart, SelectionPart, SelectionStatus } from './useGetOrderDetail';
import { OrderDrawerResponse, SelectionPart as DrawerSelectionPart } from './useGetOrderDrawer';
import { invalidateOrder } from './invalidateOrder';

interface TogglePartExclusionInput {
    orderPartId: string;
    orderId: string;
    isAssembly?: boolean;
    partNumber?: string;
    origin: 'quoting' | 'ordering';
    action?: 'toggle' | 'exclude-quoting';
}

interface TogglePartExclusionResponse {
    showQuotingPopup: boolean;
}

interface BulkToggleQuotingExclusionInput {
    orderId: string;
    parts: QuotePart[];
    exclude: boolean;
}

interface BulkToggleQuotingExclusionResponse {
    showQuotingPopup: boolean;
}

const isQuotingExcluded = (part: QuotePart) => Boolean(part.excludeQuoting || part.quoteAndExclude);

const applyQuotingExclusionState = (part: QuotePart, exclude: boolean): QuotePart => {
    if (isQuotingExcluded(part) === exclude) return part;

    if (exclude) {
        if (part.partNumber) {
            return { ...part, quoteAndExclude: true, isDuplicate: false };
        }
        return { ...part, excludeQuoting: true, isDuplicate: false };
    }

    if (part.quoteAndExclude) {
        return { ...part, quoteAndExclude: false, excludeQuoting: false };
    }
    return { ...part, excludeQuoting: false, isDuplicate: false };
};

const togglePartExclusion = async (data: TogglePartExclusionInput, sessionId: string | null): Promise<AxiosResponse<TogglePartExclusionResponse>> => {
    return axios.post(`${API_URL}/order/part/exclusion`, { ...data, newVersion: true }, {
        headers: {
            Authorization: sessionId
        }
    });
};

export const useTogglePartExclusion = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: TogglePartExclusionInput) => togglePartExclusion(data, sessionContext?.sessionId ?? null),
        onMutate: async ({ orderPartId, orderId, origin, action }: TogglePartExclusionInput) => {
            await queryClient.invalidateQueries({ queryKey: ['quoteData', orderId] });

            let excluded: boolean | undefined;

            // Optimistic update for order details
            await queryClient.setQueryData(['/order/detail', orderId], (old: OrderDetailResponse | undefined): OrderDetailResponse | undefined => {
                if (!old) return old;

                if (origin === 'quoting' && old.newParts) {
                    const part = old.newParts.parts.find((p: QuotePart) => p._id === orderPartId);
                    if (part) {
                        excluded = action === 'exclude-quoting'
                            ? !part.excludeQuoting
                            : part.quoteAndExclude ? false : !part.excludeQuoting;
                    }
                    return {
                        ...old,
                        newParts: {
                            parts: old.newParts.parts.map((part: QuotePart) => {
                                if (part._id !== orderPartId) return part;
                                if (action === 'exclude-quoting') {
                                    return part.excludeQuoting
                                        ? { ...part, excludeQuoting: false, quoteAndExclude: true }
                                        : { ...part, excludeQuoting: true, quoteAndExclude: false };
                                }
                                if (part.quoteAndExclude) {
                                    return { ...part, quoteAndExclude: false, excludeQuoting: false };
                                }
                                if (part.partNumber && !part.excludeQuoting) {
                                    return { ...part, quoteAndExclude: true, isDuplicate: false };
                                }
                                return { ...part, excludeQuoting: !part.excludeQuoting, isDuplicate: false };
                            }),
                        }
                    };
                }

                if (origin === 'ordering' && old.selection) {
                    let movedPart: SelectionPart | null = null;
                    let sourceTabKey: string | null = null;

                    for (const tab of old.selection) {
                        if (tab.key === 'all') continue;
                        const found = tab.parts.find((p) => p.partId === orderPartId);
                        if (found) {
                            movedPart = found;
                            sourceTabKey = tab.key;
                            break;
                        }
                    }

                    if (!movedPart || !sourceTabKey) return old;

                    const isExcluding = !movedPart.excludeOrdering;
                    excluded = isExcluding;
                    const updatedPart: SelectionPart = {
                        ...movedPart,
                        excludeOrdering: isExcluding,
                        selectionStatus: isExcluding ? 'excluded' as SelectionStatus : 'pending' as SelectionStatus
                    };

                    const targetTabKey = isExcluding ? 'excluded' : 'pending';

                    let updatedSelection = old.selection.map((tab) => {
                        if (tab.key === 'all') {
                            return {
                                ...tab,
                                parts: tab.parts.map((p) =>
                                    p.partId === orderPartId ? updatedPart : p
                                )
                            };
                        }
                        if (tab.key === sourceTabKey) {
                            return {
                                ...tab,
                                parts: tab.parts.filter((p) => p.partId !== orderPartId),
                                total: tab.total - 1
                            };
                        }
                        if (tab.key === targetTabKey) {
                            return {
                                ...tab,
                                parts: [...tab.parts, updatedPart],
                                total: tab.total + 1
                            };
                        }
                        return tab;
                    });

                    if (!updatedSelection.find((tab) => tab.key === targetTabKey)) {
                        updatedSelection.push({
                            key: targetTabKey,
                            title: isExcluding ? 'Excluded' : 'Pending',
                            total: 1,
                            parts: [updatedPart]
                        });
                    }

                    updatedSelection = updatedSelection.filter((tab) => tab.key === 'all' || tab.parts.length > 0);

                    return {
                        ...old,
                        selection: updatedSelection
                    };
                }

                return old;
            });

            // Optimistic update for order drawer
            await queryClient.setQueryData(['/order/drawer', orderId], (old: OrderDrawerResponse | undefined): OrderDrawerResponse | undefined => {
                if (!old) return old;

                if (origin === 'ordering' && old.selection) {
                    const updateSection = (parts: DrawerSelectionPart[] | undefined) => {
                        if (!parts) return parts;
                        return parts.map((part: DrawerSelectionPart) =>
                            part._id === orderPartId
                                ? {
                                    ...part,
                                    excludeOrdering: !part.excludeOrdering
                                }
                                : part
                        );
                    };

                    return {
                        ...old,
                        selection: {
                            ...old.selection,
                            pending: old.selection.pending ? { ...old.selection.pending, parts: updateSection(old.selection.pending.parts) || [] } : undefined,
                            flagged: old.selection.flagged ? { ...old.selection.flagged, parts: updateSection(old.selection.flagged.parts) || [] } : undefined,
                            noQuoted: old.selection.noQuoted ? { ...old.selection.noQuoted, parts: updateSection(old.selection.noQuoted.parts) || [] } : undefined,
                        }
                    };
                }

                return old;
            });

            return { excluded };
        },
        onError: (_error, { orderId }) => {
            invalidateOrder(queryClient, orderId);
        },
        onSuccess: async (_data, variables, context) => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['/order/detail', variables.orderId] }),
                queryClient.invalidateQueries({ queryKey: ['/order/drawer', variables.orderId] }),
                queryClient.invalidateQueries({ queryKey: ['/order/part', variables.orderPartId] }),
            ]);
        },
    });
};

export const useBulkToggleQuotingExclusion = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ orderId, parts, exclude }: BulkToggleQuotingExclusionInput): Promise<BulkToggleQuotingExclusionResponse> => {
            const partsToToggle = parts.filter((part) => isQuotingExcluded(part) !== exclude);
            const { data } = await axios.post<{ showQuotingPopup: boolean; changed: number }>(
                `${API_URL}/order/parts/exclusion`,
                {
                    orderId,
                    orderPartIds: partsToToggle.map((part) => part._id),
                    origin: 'quoting',
                    exclude,
                    newVersion: true,
                },
                { headers: { Authorization: sessionContext?.sessionId ?? null } }
            );
            return { showQuotingPopup: data.showQuotingPopup };
        },
        onMutate: async ({ orderId, exclude }: BulkToggleQuotingExclusionInput) => {
            await queryClient.invalidateQueries({ queryKey: ['quoteData', orderId] });

            await queryClient.setQueryData(['/order/detail', orderId], (old: OrderDetailResponse | undefined): OrderDetailResponse | undefined => {
                if (!old?.newParts) return old;

                return {
                    ...old,
                    newParts: {
                        ...old.newParts,
                        parts: old.newParts.parts.map((part) => applyQuotingExclusionState(part, exclude)),
                    },
                };
            });
        },
        onError: (_error, { orderId }) => {
            invalidateOrder(queryClient, orderId);
        },
        onSuccess: async (_data, { orderId }) => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['/order/detail', orderId] }),
                queryClient.invalidateQueries({ queryKey: ['/order/drawer', orderId] }),
            ]);
        },
    });
};
